<template>
  <div class="terms-array-editor">
    <!-- 條款項目列表 -->
    <div
      v-for="(item, index) in termsItems"
      :key="index"
      class="term-item mb-6"
    >
      <div class="term-header d-flex align-items-center mb-6">
        <span class="term-order badge badge-primary mr-2">{{ index + 1 }}</span>
        <b-form-input
          v-model="item.title"
          placeholder="請輸入條款標題"
          class="term-title-input flex-grow-1"
          @input="emitUpdate"
        />
        <div class="term-actions d-flex flex-shrink-0 ml-3">
          <b-button
            v-if="index > 0"
            variant="outline-secondary"
            class="mr-2"
            @click="moveUp(index)"
            title="上移"
          >
            <i class="mdi mdi-arrow-up" />
          </b-button>
          <b-button
            v-if="index < termsItems.length - 1"
            variant="outline-secondary"
            class="mr-2"
            @click="moveDown(index)"
            title="下移"
          >
            <i class="mdi mdi-arrow-down" />
          </b-button>
          <b-button
            variant="outline-danger"
            @click="removeItem(index)"
            title="刪除此條款"
          >
            <i class="mdi mdi-delete" />
          </b-button>
        </div>
      </div>

      <div class="term-meta">
        <b-form-row>
          <b-col cols="12" lg="6">
            <b-form-group
              label="版本名稱"
              label-size="sm"
              label-class="term-meta-label"
              class="mb-2"
            >
              <b-form-input
                v-model="item.code"
                size="sm"
                placeholder="例如：v1 條款"
                @input="emitUpdate"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" lg="6">
            <b-form-group
              label="版本號"
              label-size="sm"
              label-class="term-meta-label"
              class="mb-2"
            >
              <b-form-input
                v-model="item.version"
                size="sm"
                placeholder="例如：1.0.0"
                @input="emitUpdate"
              />
            </b-form-group>
          </b-col>
        </b-form-row>
      </div>
      
      <div class="term-content">
        <!-- 使用 CKEditor 進行富文字編輯 -->
        <ckeditor
          v-if="useEditor"
          :editor="editor"
          :config="editorConfig"
          v-model="item.content_text"
          @input="emitUpdate"
        />
        <!-- 使用普通 textarea（適用於不需要編輯器的場景，如步驟區域） -->
        <b-form-textarea
          v-else
          v-model="item.content_text"
          placeholder="請輸入內容"
          rows="4"
          max-rows="12"
          @input="emitUpdate"
        />
      </div>
    </div>

    <!-- 新增按鈕 -->
    <div class="add-term-btn mt-3">
      <b-button variant="outline-primary" @click="addItem">
        <i class="mdi mdi-plus mr-1" /> 新增條款項目
      </b-button>
    </div>

    <!-- 預覽/原始碼切換 -->
    <div class="mt-4">
      <b-button
        variant="link"
        size="sm"
        class="p-0 text-muted"
        @click="showRawJson = !showRawJson"
      >
        <i class="mdi mdi-code-json mr-1" />
        {{ showRawJson ? '隱藏' : '顯示' }} JSON 原始碼
      </b-button>
      <div v-if="showRawJson" class="mt-2">
        <JsonEditor
          :value="parsedJsonValue"
          @input="handleJsonEditorInput"
          height="400px"
          :options="{ wrap: true }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import JsonEditor from "@/components/Dashboard/JsonEditor.vue";

export default {
  name: "TermsArrayEditor",
  components: {
    JsonEditor,
  },
  props: {
    /**
     * v-model 綁定值，接收 JSON 字串或陣列
     */
    value: {
      type: [String, Array],
      default: () => [],
    },
    /**
     * 是否使用 CKEditor 編輯器
     * 設為 false 時使用普通 textarea（適用於不需要富文字編輯的場景，如【步驟區域】）
     */
    useEditor: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      termsItems: [],
      editor: ClassicEditor,
      editorConfig: {
        toolbar: {
          items: [
            "heading",
            "|",
            "bold",
            "italic",
            "link",
            "|",
            "bulletedList",
            "numberedList",
            "|",
            "blockQuote",
            "insertTable",
            "|",
            "undo",
            "redo",
          ],
        },
        language: "zh",
      },
      showRawJson: false,
    };
  },
  computed: {
    /**
     * 給 JSON 編輯器直接使用的物件陣列
     */
    parsedJsonValue() {
      return this.termsItems.map((item, idx) => ({
        order: idx + 1,
        title: item.title,
        code: item.code,
        version: item.version,
        content_text: this.processHtml(item.content_text),
      }));
    },
    /**
     * 格式化後的 JSON 字串，供純粹輸出或檢查用（已轉換 <p> 為 <div>）
     */
    formattedJson() {
      return JSON.stringify(this.parsedJsonValue, null, 2);
    },
  },
  watch: {
    /**
     * 監聽外部 value 變化，同步到內部 termsItems
     * 若為內部觸發的更新（isInternalUpdate），則跳過解析以避免 CKEditor 重渲染導致游標跳位
     */
    value: {
      immediate: true,
      handler(newVal) {
        this.parseValue(newVal);
      },
    },
  },
  methods: {
    /**
     * 解析傳入的值，轉換為內部陣列格式
     * @param {String|Array} val - JSON 字串或陣列
     */
    parseValue(val) {
      if (!val) {
        this.termsItems = [];
        return;
      }

      let parsed = val;
      if (typeof val === "string") {
        try {
          parsed = JSON.parse(val);
        } catch (e) {
          console.error("TermsArrayEditor: 無法解析 JSON", e);
          this.termsItems = [];
          return;
        }
      }

      if (Array.isArray(parsed)) {
        // 確保每個項目都有必要的欄位，並將 <div> 轉回 <p> 以符合 CKEditor 段落模型
        const newItems = parsed.map((item, idx) => ({
          order: item.order || idx + 1,
          title: item.title || "",
          code: item.code || "",
          version: item.version || "",
          content_text: this.restoreHtmlForEditor(item.content_text || ""),
        }));

        // 如果新舊資料結構完全一致，則不進行更新，避免 CKEditor 不必要的重繪與游標跳位
        if (JSON.stringify(this.termsItems) === JSON.stringify(newItems)) {
          return;
        }

        // 就地更新 (In-place update)：盡可能對既有物件做更新，避免 v-for 產生全新的物件實例
        if (this.termsItems.length === newItems.length) {
          for (let i = 0; i < newItems.length; i++) {
            const currentItem = this.termsItems[i];
            const newItem = newItems[i];
            
            if (currentItem.title !== newItem.title) {
              currentItem.title = newItem.title;
            }
            if (currentItem.order !== newItem.order) {
              currentItem.order = newItem.order;
            }
            if (currentItem.code !== newItem.code) {
              currentItem.code = newItem.code;
            }
            if (currentItem.version !== newItem.version) {
              currentItem.version = newItem.version;
            }
            if (currentItem.content_text !== newItem.content_text) {
              currentItem.content_text = newItem.content_text;
            }
          }
        } else {
          // 筆數不同的情況（新增或刪除），直接取代
          this.termsItems = newItems;
        }
      } else {
        this.termsItems = [];
      }
    },

    /**
     * 發送更新事件，將內部資料轉為 JSON 字串
     */
    emitUpdate() {
      // 更新 order 值，並將 CKEditor 產生的 <p> 轉換回 <div>
      const result = this.termsItems.map((item, idx) => ({
        order: idx + 1,
        title: item.title,
        code: item.code,
        version: item.version,
        content_text: this.processHtml(item.content_text),
      }));
      // 輸出為 JSON 字串以符合原有的資料格式
      this.$emit("input", JSON.stringify(result, null, 2));
    },

    /**
     * 處理 HTML 內容
     * 1. 將 <p> 轉換為 <div>
     * 2. 對 <a> 標籤加入 inline styles（藍色底線）
     * @param {String} html - HTML 字串
     * @returns {String}
     */
    processHtml(html) {
      if (!html) return html
      return html
        // 將 <p> 轉換為 <div>
        .replace(/<p>/g, '<div>')
        .replace(/<\/p>/g, '</div>')
        // 對沒有 style 屬性的 <a> 標籤加入 inline styles
        .replace(/<a(?![^>]*style=)([^>]*)>/g, '<a$1 style="color:#0066cc; text-decoration:underline;">')
    },

    /**
     * 還原 HTML 為 CKEditor 可正確處理的格式
     * 將儲存時轉換的 <div> 轉回 <p>，以符合 CKEditor 5 的段落模型
     * @param {String} html - HTML 字串
     * @returns {String}
     */
    restoreHtmlForEditor(html) {
      if (!html) return html
      var result = html
        // 將 <div> 轉回 <p>，符合 CKEditor 段落模型
        .replace(/<div>/g, '<p>')
        .replace(/<\/div>/g, '</p>')
        // 將 <br> 斷行拆分為獨立段落，讓 CKEditor 可正確處理區塊格式（如 Heading）
        .replace(/<br\s*\/?>/gi, '</p><p>')
        // 剝除由 processHtml 寫入但 CKEditor 原本沒有的 inline style，確保字串雙向相等等價
        .replace(/<a([^>]*) style="color:#0066cc; text-decoration:underline;"([^>]*)>/g, '<a$1$2>')
        
      // 清除多餘的空段落
      result = result.replace(/<p>\s*<\/p>/g, '')
      return result
    },

    /**
     * 處理 JsonEditor 回傳的資料
     * @param {Array} newVal - JSON Editor 產出的新陣列物件
     */
    handleJsonEditorInput(newVal) {
      if (!Array.isArray(newVal)) return;

      // 更新內部 termsItems，將 <div> 自動轉換回 <p> 供 CKEditor 正確顯示
      const newItems = newVal.map((item, idx) => ({
        order: item.order || idx + 1,
        title: item.title || "",
        code: item.code || "",
        version: item.version || "",
        content_text: this.restoreHtmlForEditor(item.content_text || ""),
      }));

      this.termsItems = newItems;
      this.$emit("input", JSON.stringify(newVal, null, 2));
    },

    /**
     * 新增條款項目
     */
    addItem() {
      this.termsItems.push({
        order: this.termsItems.length + 1,
        title: "",
        code: "",
        version: "",
        content_text: "",
      });
      this.emitUpdate();
    },

    /**
     * 移除指定條款項目
     * @param {Number} index - 項目索引
     */
    removeItem(index) {
      this.$swal
        .fire({
          title: "確定要刪除此條款嗎？",
          text: "刪除後將無法復原",
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "確定刪除",
          cancelButtonText: "取消",
        })
        .then((result) => {
          if (result.value) {
            this.termsItems.splice(index, 1);
            this.emitUpdate();
          }
        });
    },

    /**
     * 將條款項目上移
     * @param {Number} index - 項目索引
     */
    moveUp(index) {
      if (index > 0) {
        const temp = this.termsItems[index];
        this.$set(this.termsItems, index, this.termsItems[index - 1]);
        this.$set(this.termsItems, index - 1, temp);
        this.emitUpdate();
      }
    },

    /**
     * 將條款項目下移
     * @param {Number} index - 項目索引
     */
    moveDown(index) {
      if (index < this.termsItems.length - 1) {
        const temp = this.termsItems[index];
        this.$set(this.termsItems, index, this.termsItems[index + 1]);
        this.$set(this.termsItems, index + 1, temp);
        this.emitUpdate();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.terms-array-editor {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  background-color: #fafafa;
}

.term-item {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.term-order {
  font-size: 0.9rem;
  min-width: 28px;
  text-align: center;
}

.term-title-input {
  font-weight: 600;
}

.term-meta {
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.term-meta-label {
  margin-bottom: 0.25rem;
  color: #6c757d;
  font-weight: 600;
}

.term-content {
  margin-top: 0.5rem;
}

.term-content::v-deep .ck-editor__editable {
  min-height: 150px;
  max-height: 400px;
  overflow-y: auto;

  // 縮小段落與標題的預設間距，讓 Enter 換行看起來比較緊密
  p, h1, h2, h3, h4, h5, h6 {
    margin-bottom: 0.35rem;
    margin-top: 0;
  }

  // 調整標題大小，避免預設字體太巨大
  h1 { font-size: 1.5rem; }
  h2 { font-size: 1.4rem; }
  h3 { font-size: 1.3rem; }
  h4 { font-size: 1.2rem; }
  h5 { font-size: 1.1rem; }
  h6 { font-size: 1rem; }

  // 連結樣式：藍色底線
  a {
    color: #0066cc !important;
    text-decoration: underline !important;
  }
}

.add-term-btn {
  text-align: center;
}

.raw-json-preview {
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 1rem;
  border-radius: 4px;
  font-size: 0.8rem;
  max-height: 300px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
